import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listMonitoredConversations } from '../../services/adminApi';
import useMonitoringWebSocket from '../hooks/useMonitoringWebSocket';
import './MonitoringDashboard.css';

const PAGE_SIZE = 50;

const CHANNEL_LABELS = {
  whatsapp: 'WhatsApp',
  facebook_dm: 'Facebook DM',
  facebook_comment: 'Facebook commenti',
  instagram_dm: 'Instagram DM',
  instagram_comment: 'Instagram commenti',
};

function sentimentBadgeClass(s) {
  if (s === 'positive') return 'pos';
  if (s === 'negative') return 'neg';
  if (s === 'mixed') return 'mix';
  return 'neu';
}

export default function MonitoringConversations() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [freshIds, setFreshIds] = useState(new Set());

  const [filters, setFilters] = useState({
    channel: '',
    sentiment: '',
    escalation_status: '',
    is_problematic: '',
    owner_user_id: '',
  });

  const { status: wsStatus } = useMonitoringWebSocket(
    useCallback((msg) => {
      // Highlight rows matched by a new_message event so the operator
      // can see activity at a glance. The actual row content refreshes
      // on the next fetch (we debounce to avoid hammering the API).
      if (msg?.type === 'new_message' && msg.data?.conversation_id) {
        setFreshIds((prev) => {
          const next = new Set(prev);
          next.add(msg.data.conversation_id);
          return next;
        });
        setTimeout(() => {
          setFreshIds((prev) => {
            const next = new Set(prev);
            next.delete(msg.data.conversation_id);
            return next;
          });
        }, 4000);
      }
    }, [])
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (filters.channel) params.channel = filters.channel;
      if (filters.sentiment) params.sentiment = filters.sentiment;
      if (filters.escalation_status)
        params.escalation_status = filters.escalation_status;
      if (filters.is_problematic === 'true') params.is_problematic = true;
      if (filters.is_problematic === 'false') params.is_problematic = false;
      if (filters.owner_user_id) params.owner_user_id = filters.owner_user_id;
      const res = await listMonitoredConversations(params);
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
    } catch (err) {
      console.error('Failed to load monitored conversations:', err);
      setError(
        err.response?.data?.detail ||
          'Impossibile caricare le conversazioni monitorate.'
      );
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh on burst of WS events.
  useEffect(() => {
    if (wsStatus !== 'open') return;
    const t = setInterval(() => {
      load();
    }, 30000); // safety net poll if WS misses an event
    return () => clearInterval(t);
  }, [wsStatus, load]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="monitoring-page">
      <h1>
        Conversazioni monitorate
        <span className={`live-badge ${wsStatus}`}>
          <span className="dot" />
          {wsStatus === 'open' ? 'Live' : wsStatus}
        </span>
      </h1>
      <div className="subtitle">
        Filtra le conversazioni cross-tenant. Le righe in giallo sono state
        appena aggiornate via WebSocket; le righe rosa sono problematiche.
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="filters-bar">
        <label>
          Canale
          <select
            value={filters.channel}
            onChange={(e) => setFilter('channel', e.target.value)}
          >
            <option value="">Tutti</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="facebook_dm">Facebook DM</option>
            <option value="facebook_comment">Facebook commenti</option>
            <option value="instagram_dm">Instagram DM</option>
            <option value="instagram_comment">Instagram commenti</option>
          </select>
        </label>
        <label>
          Sentiment
          <select
            value={filters.sentiment}
            onChange={(e) => setFilter('sentiment', e.target.value)}
          >
            <option value="">Tutti</option>
            <option value="positive">Positivo</option>
            <option value="neutral">Neutro</option>
            <option value="negative">Negativo</option>
            <option value="mixed">Misto</option>
          </select>
        </label>
        <label>
          Escalation
          <select
            value={filters.escalation_status}
            onChange={(e) => setFilter('escalation_status', e.target.value)}
          >
            <option value="">Tutte</option>
            <option value="active">Attive</option>
            <option value="sla_breached">SLA superato</option>
            <option value="none">Senza escalation</option>
          </select>
        </label>
        <label>
          Stato
          <select
            value={filters.is_problematic}
            onChange={(e) => setFilter('is_problematic', e.target.value)}
          >
            <option value="">Tutte</option>
            <option value="true">Solo problematiche</option>
            <option value="false">Solo OK</option>
          </select>
        </label>
        <label>
          Owner ID
          <input
            type="number"
            placeholder="user_id"
            value={filters.owner_user_id}
            onChange={(e) => setFilter('owner_user_id', e.target.value)}
            style={{ width: 90 }}
          />
        </label>
        <div className="toolbar-right">
          <button className="btn" onClick={load} disabled={loading}>
            {loading ? 'Caricamento…' : 'Aggiorna'}
          </button>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="conversations-table">
          <thead>
            <tr>
              <th>Canale</th>
              <th>Contatto</th>
              <th>Owner</th>
              <th>Sentiment</th>
              <th>Stato</th>
              <th>Anteprima</th>
              <th>Ultimo messaggio</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={7}>
                  <div className="empty-state">Nessuna conversazione corrisponde ai filtri.</div>
                </td>
              </tr>
            ) : (
              items.map((c) => (
                <tr
                  key={c.id}
                  className={`${c.is_problematic ? 'problematic' : ''} ${freshIds.has(c.id) ? 'live-fresh' : ''}`}
                  onClick={() => navigate(`/admin/monitoring/conversations/${c.id}`)}
                >
                  <td>
                    <span className="badge channel">
                      {CHANNEL_LABELS[c.channel] || c.channel}
                    </span>
                  </td>
                  <td>{c.contact_name || c.contact_id}</td>
                  <td>#{c.user_id}</td>
                  <td>
                    {c.overall_sentiment ? (
                      <span className={`badge ${sentimentBadgeClass(c.overall_sentiment)}`}>
                        {c.overall_sentiment}
                      </span>
                    ) : (
                      <span className="badge neu">—</span>
                    )}
                  </td>
                  <td>
                    {c.is_problematic ? (
                      <span className="badge problematic">problematica</span>
                    ) : null}{' '}
                    {c.active_escalations_count > 0 ? (
                      <span className="badge escalated">
                        esc {c.active_escalations_count}
                      </span>
                    ) : null}{' '}
                    {c.sla_breached ? (
                      <span className="badge sla">SLA</span>
                    ) : null}
                  </td>
                  <td style={{ maxWidth: 320, color: '#444' }}>
                    {(c.last_message_preview || '').slice(0, 90)}
                  </td>
                  <td className="meta">
                    {c.last_message_at
                      ? new Date(c.last_message_at).toLocaleString('it-IT')
                      : '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <span style={{ fontSize: 13, color: '#666' }}>
          Pagina {page} di {totalPages} — totale candidati: {total}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Precedente
          </button>
          <button
            className="btn"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Successiva
          </button>
        </div>
      </div>
    </div>
  );
}
